import { participantsToOptions } from '@/api/userSearch/useCcBccUserOptions';
import {
  planRecipientOptions,
  planSenderOptions,
} from '../../initialValues/planRecipientOptions';
/**
 * Slot Selection Email step — self-schedule modes only.
 * Full-width single email editor for sending the self-schedule link to the candidate.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { buildEditorPreviewHtml } from '@kula-ai/components';
import { Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import type {
  EmailApiHandle,
  EmailCompositionValue,
  EmailFieldErrors,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import type { InterviewEditorAttachmentItem } from '../../../InterviewActivity/communication/types';
import {
  cleanupStaleSenderValues,
  emailCompositionToCommConfig,
  attachEditorItemsToCommConfig,
  patchEmailComposition,
  getDefaultEmailComposition,
  isBlankRichText,
} from '../../../InterviewActivity/communication/helpers/emailUtils';
import {
  compositionFromTemplateOption,
  mapTemplateAttachmentsToEditorItems,
} from '../../../InterviewActivity/communication/helpers/templateApply';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import {
  parseSharedEmailId,
  senderPickerOption,
  defaultFromValueForCurrentUser,
  useScheduleSenderOptions,
} from '../../hooks/useScheduleSenderOptions';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/copy';
import type { ScheduleStepProps } from '../../config/types';
import { validateSlotSelectionEmail } from './slotSelectionEmail.validate';
import { getSenderFieldError } from '../../config/senderValidity';
import { SENDER_USE_CASE } from '../../config/senderErrorCopy';

/** Calendar/email previews share a legacy BE quirk where unresolved variables
 *  arrive tagged with `style="color:red"`. Strip it so the components-side
 *  chip styling wins. */
const buildPreviewHtml = (
  html: string,
  resolved: Record<string, string>
): string =>
  buildEditorPreviewHtml(html, resolved, { stripInlineColorRed: true });

export const SlotSelectionEmailStep = memo(
  forwardRef<
    StepFormApi,
    ScheduleStepProps & {
      candidateName?: string;
      jobTitle?: string;
      interviewPlan?: Record<string, unknown>;
    }
  >(
    (
      {
        payloadRef,
        mode: _mode,
        apiReference: _apiReference,
        prefillVersion = 0,
        candidateEmail = '',
        bulkSelfScheduleApplicationCount,
        candidateName,
        jobTitle,
        interviewPlan,
      },
      ref
    ) => {
      // The To row is always hidden in the UI (`showTo={false}` on
      // TemplatedEmail) — there's no recipient-choice UX in any flow.
      // This flag still controls whether the sync effect seeds
      // `value.to` and whether validation requires it:
      //   - Bulk self-schedule with >1 candidate: backend derives
      //     recipients from `ats_application_ids[]`, so seeding a
      //     single email + requiring it would both be meaningless.
      //   - Every other flow: the candidate's email is the recipient,
      //     so we still seed it and validate it.
      const hideToField = (bulkSelfScheduleApplicationCount ?? 0) > 1;
      const copy = SCHEDULE_INTERVIEW_COPY.slotSelectionEmail;

      // Only the candidate receives this email, so the `To` picker is
      // a single-option select whose one entry is the candidate's
      // email. Keep the value stable across renders.
      const toOptions = useMemo(() => {
        if (!candidateEmail) return [];
        return [
          {
            value: candidateEmail,
            label: candidateEmail,
          },
        ];
      }, [candidateEmail]);

      const currentUser = useSelector(selectCurrUser) as
        | {
            id?: number | string;
            first_name?: string;
            last_name?: string;
            email?: string;
          }
        | undefined;

      // ---- Preview state ----
      const [previewHtml, setPreviewHtml] = useState<string | undefined>(
        undefined
      );

      // ---- Data fetching ----
      const { data: templateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      // Slot-selection email opts into shared-mailbox sender options
      // — `mapSlotEmail` knows how to route them via
      // `candidate_mail_ats_shared_email_id`. The comm step
      // (`CommunicationStep`) does NOT opt in: its CORE-4040 nested
      // configs only accept numeric user ids on the backend permit
      // list today, so a `shared:${id}` form value would silently
      // drop. See `useScheduleSenderOptions` JSDoc.
      // Saved recipients label their own chips, and give the id → email map a
      // source for anyone outside the first search page (ENG-3766).
      const savedCcBccOptions = useMemo(() => {
        const cfg = (payloadRef.current?.slotSelectionEmail ?? {}) as Record<
          string,
          any
        >;
        // The slot-email mappers keep ids only, so the raw plan is the only
        // place the participant objects survive.
        return [
          ...planRecipientOptions(interviewPlan),
          ...participantsToOptions(cfg?.cc_participants),
          ...participantsToOptions(cfg?.bcc_participants),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion, interviewPlan]);

      // Same for the saved sender — it may not be delegatable any more.
      const savedFromOptions = useMemo(() => {
        const cfg = (payloadRef.current?.slotSelectionEmail ?? {}) as Record<
          string,
          any
        >;
        return [
          // The scheduler leads: From is seeded with them whether or not they
          // can send, so without their option the field renders blank over
          // their own id (ENG-3916).
          ...senderPickerOption(currentUser),
          ...planSenderOptions(interviewPlan),
          ...participantsToOptions([cfg?.sender]),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion, interviewPlan, currentUser]);

      const {
        fromOptions,
        validFromValues,
        ccBccOptions,
        ccBccSelectProps,
        fromSelectProps,
        getCcBccEmailByValue,
        isFromOptionsFetched,
        isFromOptionsLoading,
      } = useScheduleSenderOptions({
        includeSharedMailboxes: true,
        knownCcBccOptions: savedCcBccOptions,
        knownFromOptions: savedFromOptions,
      });

      // Name + status of the sender the interview was saved with. Once they
      // stop being delegatable the picker can no longer describe them, so the
      // error message has to come from here (ENG-3916).
      const savedSender = useMemo(() => {
        const cfg = (payloadRef.current?.slotSelectionEmail ?? {}) as Record<
          string,
          any
        >;
        return cfg?.senderParticipant ?? cfg?.sender ?? undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion]);

      // The default `from` is the scheduler, delegatable or not. Gating it on
      // the delegatable list left a recruiter whose own mailbox isn't connected
      // with an empty required field and no idea why; the sender check now says
      // so on the field, and their option above keeps the Select labelled.
      const defaultFromValue = useMemo(
        () => defaultFromValueForCurrentUser(currentUser?.id),
        [currentUser]
      );

      // ---- Email state ----
      const [value, setValue] = useState<EmailCompositionValue>(() => {
        const existing = payloadRef.current.slotSelectionEmail;
        if (existing && typeof existing === 'object' && 'content' in existing) {
          const hydrated = existing as unknown as EmailCompositionValue;
          const patched: Partial<EmailCompositionValue> = {};
          if (!hydrated.to && candidateEmail) patched.to = candidateEmail;
          if (!hydrated.from) patched.from = defaultFromValue;
          return Object.keys(patched).length > 0
            ? { ...hydrated, ...patched }
            : hydrated;
        }
        return patchEmailComposition(getDefaultEmailComposition(), {
          from: defaultFromValue,
          to: candidateEmail,
        });
      });
      // Seeded from the rows the plan's saved email carries, the same source
      // every other email section uses (`payloadMap.ts#mapEmailAttachments`).
      // `TemplatedEmail` only seeds chips for a template the picker can select,
      // and a config pointing straight at a library template has no selectable
      // id — so its files showed nowhere (ENG-4075). `_ui_attachment_items` is
      // what this step wrote last, so cross-step navigation keeps them too.
      const [attachments, setAttachments] = useState<
        InterviewEditorAttachmentItem[]
      >(() => {
        const existing = payloadRef.current.slotSelectionEmail as
          | Record<string, unknown>
          | undefined;
        const restored = existing?._ui_attachment_items;
        if (Array.isArray(restored)) {
          return restored as InterviewEditorAttachmentItem[];
        }
        // Whatever the saved config carries IS the chip list — including files
        // a recruiter added on top of the library template it was cloned from
        // (ENG-4075). Seeding a non-empty list also makes `TemplatedEmail`'s
        // mount-time guard bail ("a restored config keeps what it saved")
        // instead of overwriting these with the picked template's own files.
        //
        // Sending them back is what `hydratedTemplateIdRef` below is for: the
        // backend resolves `candidate_mail_attachments` against the record
        // named by `candidate_mail_template_id` and silently drops any id that
        // record doesn't own, so the config copy has to be named as the clone
        // source for as long as these are the rows on screen.
        return mapTemplateAttachmentsToEditorItems(
          existing?._hydrated_attachments
        ) as InterviewEditorAttachmentItem[];
      });
      // ---- Clone source for the attachments on screen ----
      // The backend resolves `candidate_mail_attachments` against the record
      // named by `candidate_mail_template_id` and silently drops every id that
      // record doesn't own (`AtsCommTemplate#resolve_attachables`). So the id
      // we send has to be whichever record owns the chips currently shown.
      //
      // Untouched picker → the chips are the saved config's own rows, owned by
      // the config copy (`_hydrated_template_id`), NOT by the library template
      // the picker displays. Naming the library one there dropped every file
      // the recruiter had added on top of it (ENG-4075).
      //
      // Once the picker is touched, `TemplatedEmail` reseeds the chips from the
      // picked template's files, so that template owns them — and permanently,
      // since switching back to the original reseeds from the library row
      // rather than restoring the config copy's.
      //
      // Captured once, on the first render: `syncToPayload` writes the live
      // picker value straight back to `payloadRef`, so re-reading it later
      // would compare the hydrated value against itself and never register a
      // switch. Adhoc mounts with no saved config at all, leaving the id null
      // and the picker value the only answer — which is the right one there.
      const hydratedSlotEmailRef = useRef<{
        templateId: number | null;
        picker: string | null;
      } | null>(null);
      if (hydratedSlotEmailRef.current === null) {
        const existing = payloadRef.current.slotSelectionEmail as
          | Record<string, unknown>
          | undefined;
        hydratedSlotEmailRef.current = {
          templateId:
            typeof existing?._hydrated_template_id === 'number'
              ? existing._hydrated_template_id
              : null,
          picker: (existing?.template as string | null) ?? null,
        };
      }
      const templateTouchedRef = useRef(false);
      useEffect(() => {
        if (templateTouchedRef.current) return;
        if ((value.template ?? null) !== hydratedSlotEmailRef.current?.picker) {
          templateTouchedRef.current = true;
        }
      }, [value.template]);

      const [fieldErrors, setFieldErrors] = useState<
        EmailFieldErrors | undefined
      >();
      /**
       * Bumped per `validate()` call so the Email re-fires its in-editor
       * auto-scroll on repeat submits with unchanged errors.
       */
      const [submitAttempt, setSubmitAttempt] = useState(0);

      /** Imperative handle of the slot-selection Email — `validate()`
       *  calls `focusFirstError()` on it to scroll to the bad
       *  field/variable. The shared `TemplatedEmail` opts out of the
       *  library's own validate-driven auto-scroll, so this step drives
       *  the scroll explicitly (same pattern as the comm-step panes). */
      const slotEmailApiRef = useRef<EmailApiHandle | null>(null);
      /** Dedupes rapid double-`validate()` calls (e.g. submit click +
       *  stepper re-validate) so we don't fight ourselves on the scroll. */
      const lastErrorScrollAtRef = useRef(0);

      const { selfScheduleEmail: selfScheduleVariables } =
        useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // Guarantee `to` tracks `candidateEmail` once it resolves. The
      // lazy init above runs before `applicationData.contact` may
      // arrive (async `/ats_applications/:id` fetch) and there's no
      // user-editable `To` picker, so this is a pure defensive sync.
      // Bulk multi-candidate has no single recipient — backend derives
      // them from `ats_application_ids[]` server-side — so skip the
      // sync rather than seeding a meaningless address.
      useEffect(() => {
        if (hideToField) return;
        if (!candidateEmail) return;
        if (value.to === candidateEmail) return;
        setValue((prev) => ({ ...prev, to: candidateEmail }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [candidateEmail, hideToField]);

      // Backfill `from` once both the delegatable-senders fetch AND
      // Redux `currUser` have settled. Waiting for `isFromOptionsFetched`
      // is what prevents the "id set but not in options" bug — the
      // resolver returns '' for a non-delegatable user, leaving the
      // select empty so the required-field validator can catch it.
      useEffect(() => {
        if (!isFromOptionsFetched) return;
        if (value.from) return;
        if (!defaultFromValue) return;
        setValue((prev) => ({ ...prev, from: defaultFromValue }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultFromValue, isFromOptionsFetched]);

      // After options arrive, drop any prefilled `from`/`cc`/`bcc` values
      // whose referenced user is no longer in the picker — happens when
      // a teammate named in the plan template was removed from the org.
      const didCleanupStaleRef = useRef(false);
      useEffect(() => {
        if (didCleanupStaleRef.current) return;
        if (!isFromOptionsFetched) return;
        didCleanupStaleRef.current = true;
        const fromOptionValues = new Set(
          fromOptions.map((o) => String(o.value))
        );
        // No `ccBccOptionValues`: CC/BCC searches server-side now, so the
        // loaded page is not the full list and pruning against it would delete
        // valid saved recipients (ENG-3766).
        setValue((prev) => {
          const cleaned = cleanupStaleSenderValues(prev, { fromOptionValues });
          return cleaned ?? prev;
        });
      }, [isFromOptionsFetched, fromOptions]);

      // ---- One-shot template default ----
      // Picks the first available template once IF the section hydrates
      // blank. The slot-selection email is mandatory + always on, so a
      // legacy activity (its plan predates this section) must still land
      // on the first template on edit — not a blank Subject/body.
      //
      // `isBlankRichText` is the regression guard, not the mode: a
      // plan/edit hydration with real subject/body (created in the new
      // feature) is detected as non-blank and left untouched, while
      // old/pre-revamp plans that hydrate HTML-empty (`<p></p>`, `<br>`,
      // `&nbsp;`) or whitespace-only still get the first template.
      const didDefaultTemplate = useRef(false);

      useEffect(() => {
        if (!templateOptions.length) return;
        if (value.template) {
          didDefaultTemplate.current = true;
          return;
        }
        const hasHydratedContent =
          !isBlankRichText(value.subject) || !isBlankRichText(value.content);
        if (hasHydratedContent) {
          didDefaultTemplate.current = true;
          return;
        }
        if (didDefaultTemplate.current) return;
        didDefaultTemplate.current = true;
        setValue(compositionFromTemplateOption(value, templateOptions[0]));
      }, [templateOptions, value]);

      // One options object so the live pass and the submit pass can't drift.
      const validateOptions = useMemo(
        () => ({
          skipToCheck: hideToField,
          variables: selfScheduleVariables,
          senderError: getSenderFieldError({
            from: value.from,
            validFromValues,
            savedSender,
            isFromOptionsFetched,
            currentUserId: currentUser?.id,
            useCase: SENDER_USE_CASE.slotSelected,
          }),
        }),
        [
          hideToField,
          selfScheduleVariables,
          value.from,
          validFromValues,
          savedSender,
          isFromOptionsFetched,
          currentUser,
        ]
      );

      // ---- Live validation after first failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!prev) return prev;
          return validateSlotSelectionEmail(value, value.from, validateOptions);
        });
      }, [value, validateOptions]);

      // CC/BCC for the slot-selection email must hit the wire as email
      // strings — backend looks up `User.where(email: candidate_mail_cc)`
      // and pipes the same array into the mail-job cc header
      // (`ats_schedule.rb#candidate_mail_cc_users`, `#perform`). The
      // picker uses user-id option values, so resolve id → email here
      // before write. Every other email step in the wizard sends IDs
      // (calendar-invite uses `cc_user_ids`/`cc_participants`); this
      // mismatch is specific to the slot-selection BE contract.
      // Resolved through the picker's registry rather than the current option
      // list: in async mode the list is only the last search's results, so a
      // recipient found by typing (or restored from a saved plan) would not be
      // in it and its address would silently drop from the payload.

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateSlotSelectionEmail(
              value,
              value.from,
              validateOptions
            );
            setFieldErrors(errors);
            /** Bump so the Email surfaces its in-editor errors, even when
             *  the error map is structurally identical. */
            setSubmitAttempt((n) => n + 1);

            const now = Date.now();
            const debounced = now - lastErrorScrollAtRef.current <= 250;
            lastErrorScrollAtRef.current = now;

            if (errors && !debounced) {
              // The library `TemplatedEmail` opts out of validate-driven
              // auto-scroll, so drive it explicitly. Defer so the editor
              // has settled (and, on cross-step nav, has mounted) before
              // we ask it to scroll to the first bad field/variable.
              const runScroll = () => {
                slotEmailApiRef.current?.focusFirstError();
              };
              setTimeout(runScroll, 120);
              setTimeout(runScroll, 260);
            }
            return !errors;
          },
          syncToPayload: () => {
            // Shared-mailbox sender: form stores `from` as
            // `shared:${id}` (see `useScheduleSenderOptions`).
            // `emailCompositionToCommConfig` only knows numeric user
            // ids, so substitute the current user's id as the
            // apparent `from` and stash the shared mailbox id under a
            // UI-only key for `mapSlotEmail` to pick up. This keeps
            // the InterviewActivity helper untouched (it's shared
            // with the calendar-invite step).
            const sharedEmailId = parseSharedEmailId(value.from);
            const valueForCompose =
              sharedEmailId !== undefined && currentUser?.id !== undefined
                ? { ...value, from: String(currentUser.id) }
                : value;
            const config = attachEditorItemsToCommConfig(
              emailCompositionToCommConfig(valueForCompose),
              attachments
            );
            const idsToEmails = (ids: string[] | undefined): string[] => {
              if (!Array.isArray(ids) || ids.length === 0) return [];
              const out: string[] = [];
              for (const id of ids) {
                const email = getCcBccEmailByValue(String(id));
                if (email) out.push(email);
              }
              return out;
            };
            const ccEmails = idsToEmails(value.cc);
            const bccEmails = idsToEmails(value.bcc);
            payloadRef.current.slotSelectionEmail = {
              ...config,
              cc: ccEmails,
              bcc: bccEmails,
              // The record that owns the attachment ids above — see
              // `hydratedTemplateIdRef`. Only diverges from the picker value
              // while the picker still holds exactly what hydration put there.
              _ui_template_id:
                !templateTouchedRef.current &&
                typeof hydratedSlotEmailRef.current?.templateId === 'number'
                  ? hydratedSlotEmailRef.current.templateId
                  : (value.template ?? null),
              _ui_attachment_items: attachments,
              ...(sharedEmailId !== undefined
                ? { _shared_email_id: sharedEmailId }
                : {}),
            } as unknown as Record<string, unknown>;
          },
        }),
        [
          attachments,
          getCcBccEmailByValue,
          currentUser?.id,
          payloadRef,
          validateOptions,
          value,
        ]
      );

      const buildSlotEmailVars = useCallback((): Record<string, string> => {
        const parts = candidateName
          ? candidateName.trim().split(/\s+/)
          : undefined;
        const firstName = parts?.[0];
        const lastName =
          parts && parts.length > 1 ? parts.slice(1).join(' ') : undefined;
        const senderFirst = currentUser?.first_name;
        const senderLast = currentUser?.last_name;
        const senderFull =
          senderFirst || senderLast
            ? `${senderFirst ?? ''} ${senderLast ?? ''}`.trim()
            : undefined;

        const vars: Record<string, string> = {};
        const set = (key: string, val: string | undefined) => {
          if (val !== undefined && val !== '') vars[`{{${key}}}`] = val;
        };
        set('candidate.first_name', firstName);
        set('candidate.last_name', lastName);
        set('candidate.full_name', candidateName?.trim());
        set('job.title', jobTitle);
        set('sender.first_name', senderFirst);
        set('sender.last_name', senderLast);
        set('sender.full_name', senderFull);
        set('sender.email', currentUser?.email);
        return vars;
      }, [candidateName, currentUser, jobTitle]);

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          justifyContent="center"
          overflow="auto"
        >
          <Flex
            w="100%"
            maxW="720px"
            flexDir="column"
            minH={0}
            // The kula Email organism + its internal card/stack divs
            // shrink-wrap, so the editor sits at its fixed 320px default and
            // leaves the rest of the step blank. Stretch each level (none has
            // a stable hook — target by depth) so the editor frame's
            // `height: '100%'` below can fill the available vertical space.
            sx={{
              '& > div': { flex: 1, minH: 0 },
              '& > div > div:first-of-type': { flex: 1, minH: 0 },
              '& > div > div:first-of-type > div': { flex: 1, minH: 0 },
            }}
          >
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo={false}
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              submitAttempt={submitAttempt}
              apiRef={slotEmailApiRef}
              variables={selfScheduleVariables}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              templateOptions={templateOptions}
              fromOptions={fromOptions}
              fromSelectProps={{
                ...fromSelectProps,
                loading: isFromOptionsLoading,
              }}
              toOptions={toOptions}
              toMode="single"
              ccBccOptions={ccBccOptions}
              ccSelectProps={ccBccSelectProps}
              bccSelectProps={ccBccSelectProps}
              applyTemplateBodyToEditor
              value={value}
              onChange={setValue}
              errors={fieldErrors}
              placeholders={{
                from: copy.placeholders.from,
                to: copy.placeholders.to,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
              editorProps={{
                height: '100%',
                previewHtml,
                onPreviewClick: () =>
                  setPreviewHtml(
                    buildPreviewHtml(value.content, buildSlotEmailVars())
                  ),
                onPreviewDismiss: () => setPreviewHtml(undefined),
              }}
            />
          </Flex>
        </Flex>
      );
    }
  )
);

SlotSelectionEmailStep.displayName = 'SlotSelectionEmailStep';
